/*
 * Copyright (c) 2026 Bluemate B.V.
 */

import './style/productSelection.scss';
import {
    AwesomeIcon,
    ChangeFunction,
    Checkbox,
    ElementPosition,
    getSetting,
    Loader,
    remToPx,
    voidFunction
} from '@dvrd/dvr-controls';

import {Icon} from '@iconify/react';

import classNames from 'classnames';

import React, {CSSProperties, KeyboardEventHandler, MouseEventHandler, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import BolProduct from '../../models/bolProductModel';

import {ClauseType, ProductSelectionList, ProductSelectionParam} from '../../util/types';

import {BluemateButton, BluemateCheckbox, BluemateDialog, BluemateListCard, BluemateSearchInput} from '../controls';

import InfoIcon from '../icon/infoIcon';

import {AppContext} from '../misc/appContext';

import InfiniteList, {ListLoader, RenderRowProps} from '../misc/infiniteList';

interface Props {
    onClose: VoidFunction;
    onChangeSearch: ChangeFunction<string>;
    onEnterSearch: KeyboardEventHandler;
    onClearSearch: VoidFunction;
    onImportProducts: MouseEventHandler;
    onSelectProduct: (product: BolProduct, clauseType: ClauseType) => MouseEventHandler;
    onSelectAll: (clauseType: ClauseType) => MouseEventHandler;
    onSelectRemaining: ChangeFunction<boolean>;
    onLoadMore: (listType: ProductSelectionList) => ListLoader;
    onSubmit: VoidFunction;
    availableProducts: Array<BolProduct>;
    selectedProducts: Array<BolProduct>;
    loadingMore: ProductSelectionParam<boolean>;
    loading: ProductSelectionParam<boolean>;
    parentLoading: boolean;
    search: string;
    total: ProductSelectionParam<number>;
    active: boolean;
    remainingSelected: boolean;
    allSelected: boolean;
    anySelected: boolean;
    importing: boolean;
    remainingInfoText?: string;
}

const ITEM_SIZE = remToPx(2.5);

export default function ProductSelection(props: Props) {
    const {t} = useTranslation();
    const {user} = useContext(AppContext).userContext;
    const {
        onClose,
        onChangeSearch,
        onSelectProduct,
        onSelectAll,
        onSelectRemaining,
        onLoadMore,
        onSubmit,
        availableProducts,
        selectedProducts,
        loadingMore,
        loading,
        search,
        total,
        active,
        remainingSelected,
        onEnterSearch,
        allSelected,
        anySelected,
        parentLoading,
        onImportProducts,
        importing,
        onClearSearch,
        remainingInfoText
    } = props;

    function renderBody() {
        // No wrapping .body-container here — BluemateDialog renders it for us.
        // The .product-select .body-container styles target that wrapper.
        return (
            <>
                {parentLoading && <Loader active />}
                {renderSearchBar()}
                {renderGlobalActions()}
                <div className='lists-container'>
                    {renderDisabledOverlay()}
                    <div className='all-buttons'>{renderAllButtons()}</div>
                    {renderSearchLabel()}
                    {renderLists()}
                </div>
            </>
        );
    }

    // Empty value comes from the built-in clear button (or backspacing the
    // last character): route it through onClearSearch so the reload fires
    // immediately, like the old ornament clear did, instead of debounced.
    function onSearchInput(value: string) {
        if (value.length) onChangeSearch(value);
        else onClearSearch();
    }

    function renderSearchBar() {
        const hideSyncForUsers: Array<string> = getSetting('hideProductSyncForUsers') ?? [];
        const hideSyncButton = !!user && hideSyncForUsers.includes(user?.id);
        return (
            <div className='search-bar'>
                <BluemateSearchInput
                    onChange={onSearchInput}
                    value={search}
                    label={t('productSelection.searchProduct', 'Zoek een product')}
                    placeholder={t('productSelection.searchPlaceholder', 'EAN of productnaam')}
                    // The controller refocuses the input by this id after a
                    // clear (BluemateInput used to derive it from id + '-input').
                    inputProps={{id: 'product-select-search-input'}}
                    onEnter={onEnterSearch}
                    autoFocus
                />
                {!hideSyncButton && (
                    // The label stays constant while importing (the spinner +
                    // disabled state carry the feedback), so the button keeps
                    // its natural width and the input next to it never
                    // reflows.
                    <BluemateButton
                        onClick={onImportProducts}
                        label={t('productSelection.sync', 'Sync met Bol')}
                        disabled={importing}
                        tertiary
                        color='blue'
                        shape='rounded'
                        ornaments={{
                            element: importing ? (
                                <AwesomeIcon name='rotate' className='spin' />
                            ) : (
                                <AwesomeIcon name='rotate' />
                            )
                        }}
                    />
                )}
            </div>
        );
    }

    function renderGlobalActions() {
        return (
            <div className='global-actions'>
                <div className='with-info'>
                    <BluemateCheckbox
                        onCheck={onSelectRemaining}
                        checked={remainingSelected}
                        label={t('productSelection.selectRemaining', 'Resterende producten selecteren')}
                    />
                    <div className='info-container'>
                        <InfoIcon />
                        <p className='info-text'>
                            {remainingInfoText ??
                                t(
                                    'productSelection.selectRemainingInfo',
                                    'Met deze optie worden automatisch alle producten geselecteerd waarvoor nog geen andere mail ingesteld is.'
                                )}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    function renderSearchLabel() {
        if (!search.length) return null;
        return (
            <div className='search-label-container' onClick={onClearSearch}>
                <span>
                    {t('productSelection.searchedFor', 'Gezocht op')} "{search}"
                </span>
                <AwesomeIcon name='circle-xmark' prefix='far' />
            </div>
        );
    }

    function renderDisabledOverlay() {
        if (!remainingSelected) return null;
        return (
            <div className='overlay'>
                <div className='disabled-text'>
                    <label className='disabled-title'>
                        {t('productSelection.remainingSelected', 'Resterende producten geselecteerd')}
                    </label>
                    <label>
                        {t(
                            'productSelection.remainingSelectedInfo',
                            'Linksboven het vakje "Resterende producten selecteren" om resterende/geen producten te selecteren'
                        )}
                    </label>
                </div>
            </div>
        );
    }

    function renderAllButtons() {
        const icon = <Icon icon='mdi:arrow' className='arrow-icon' />;
        return (
            <>
                <BluemateButton
                    onClick={onSelectAll('exclude')}
                    label={t('productSelection.deselectAll', 'Alles deselecteren')}
                    ornaments={{element: icon, placement: ElementPosition.LEFT}}
                    disabled={!anySelected}
                    outlined
                    size='small'
                    className='btn-all selected'
                />
                <BluemateButton
                    onClick={onSelectAll('include')}
                    label={t('productSelection.selectAll', 'Alles selecteren')}
                    ornaments={{element: icon, placement: ElementPosition.RIGHT}}
                    disabled={allSelected}
                    outlined
                    size='small'
                    className='btn-all'
                />
            </>
        );
    }

    /**
     * Width of the product-name column, in pixels: the widest name currently
     * loaded. Every row is its own CSS grid (react-window renders them
     * separately), so a content-sized track would give each row a different
     * width and the sticky header would stop lining up with the rows. One
     * measured number, handed to all of them, keeps the header and every row
     * exactly as wide as the longest one — and nothing gets cut.
     */
    const nameColumnWidth = useMemo(() => {
        const names = [...availableProducts, ...selectedProducts].map(product => product.actualTitle ?? '');
        if (!names.length) return null;
        const context = document.createElement('canvas').getContext('2d');
        if (!context) return null;
        const root = getComputedStyle(document.documentElement);
        // Matches `.list-row` on mobile (0.85rem) — the only place this scrolls.
        context.font = `${0.85 * parseFloat(root.fontSize)}px ${root.fontFamily}`;
        const widest = names.reduce((max, name) => Math.max(max, context.measureText(name).width), 0);
        return `${Math.ceil(widest) + 8}px`;
    }, [availableProducts, selectedProducts]);

    function renderLists() {
        return (
            <div
                className='product-lists'
                style={nameColumnWidth ? ({'--product-name-col': nameColumnWidth} as CSSProperties) : undefined}
            >
                {renderListCard('include')}
                {renderListCard('exclude')}
            </div>
        );
    }

    // One bordered card per list (available | selected): header with title +
    // count, virtualised rows below. `clauseType` is what clicking a row DOES
    // (include = add to selection), so the available list is the 'include' one.
    function renderListCard(clauseType: ClauseType) {
        const isSelected = clauseType === 'exclude';
        const products = isSelected ? selectedProducts : availableProducts;
        const listTotal = isSelected ? total.selected : total.available;
        const title = isSelected
            ? t('productSelection.selectedProducts', 'Geselecteerde producten')
            : t('productSelection.availableProducts', 'Beschikbare producten');
        const showTotals = total.selected > -1 && total.available > -1;
        return (
            <BluemateListCard
                // On a phone the EAN and the product name cannot both fit: the name
                // was cut mid-word with no way to read the rest. Let the row keep
                // its natural width and scroll sideways instead.
                scrollX
                className={classNames('product-list-card', isSelected ? 'selected' : 'available')}
                title={`${title}${
                    search.length ? ` ${t('productSelection.withSearchTerm', 'met zoekterm')} '${search}'` : ''
                }`}
                count={showTotals ? listTotal : undefined}
            >
                <InfiniteList
                    onLoadMore={onLoadMore(isSelected ? 'selected' : 'available')}
                    renderRow={renderListRow(clauseType)}
                    hasMore={products.length < listTotal}
                    isLoadingMore={isSelected ? loadingMore.selected : loadingMore.available}
                    itemCount={products.length}
                    itemSize={ITEM_SIZE}
                    stickyIndexes={[0]}
                    renderStickyRow={renderListHead}
                />
            </BluemateListCard>
        );
    }

    function renderListHead(props: RenderRowProps) {
        return (
            <div key={props.index} style={props.style} className='default-table-row list-row head'>
                <div />
                <label>{t('productSelection.ean', 'EAN')}</label>
                <label>{t('productSelection.productName', 'Productnaam')}</label>
            </div>
        );
    }

    function renderListRow(clauseType: ClauseType) {
        return function (props: RenderRowProps) {
            const {style, index, isLoaded} = props;
            if (!isLoaded)
                return (
                    <div style={style} className='default-table-row list-row'>
                        <div />
                        <label>{t('productSelection.loadingProduct', 'Product laden...')}</label>
                        <label />
                    </div>
                );
            const isSelected = clauseType === 'exclude';
            const useList: Array<BolProduct> = isSelected ? selectedProducts : availableProducts;
            const product = useList[index];
            if (!product) return <div />;
            return (
                <div
                    key={index}
                    style={style}
                    className='default-table-row list-row'
                    onClick={onSelectProduct(product, clauseType)}
                >
                    <Checkbox onCheck={voidFunction} checked={isSelected} noRipple />
                    <label>{product.ean}</label>
                    <label>{product.actualTitle}</label>
                </div>
            );
        };
    }

    const submitText = search.length ? t('productSelection.next', 'Volgende') : t('productSelection.done', 'Klaar');
    const submit = search.length ? onClearSearch : onSubmit;
    return (
        <BluemateDialog
            active={active}
            onClose={onClose}
            title={t('productSelection.title', 'Producten selecteren')}
            className='product-select'
            width='80rem'
            onConfirm={submit}
            confirmLabel={submitText}
            confirmDisabled={loading.available || loading.selected}
            cancelVariant='tertiary'
        >
            {renderBody()}
        </BluemateDialog>
    );
}

ProductSelection.displayName = 'ProductSelection';
